import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import NavInf from "../../components/NavInf/NavInf";
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faGripVertical,
    faWallet,
    faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";
import "./SAI.css";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { secureFetch } from '../../utils/secureFetch';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import Loader from '../../components/Loader/Loader';

const SAI = () => {
    const [montoTotal, setMontoTotal] = useState("");
    const [proveedores, setProveedores] = useState([]);
    const [modalIdx, setModalIdx] = useState(null);
    const [abonoSim, setAbonoSim] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                secureFetch('https://api.minymol.com/debts/providers')
                    .then(res => {
                        if (!res.ok) throw new Error('Error cargando proveedores');
                        return res.json();
                    })
                    .then(data => {
                        // Transformar los datos del backend al formato que usa SAI
                        const proveedoresFormateados = data.map(prov => ({
                            id: prov.id,
                            nombre: prov.name,
                            deuda: prov.deudaRestante,
                            abono: 0
                        }));
                        setProveedores(proveedoresFormateados);
                        setLoading(false);
                    })
                    .catch(err => {
                        console.error(err);
                        setProveedores([]);
                        setLoading(false);
                    });
            }
        });
        return unsubscribe;
    }, []);

    // Función para formatear números con separadores de miles (puntos)
    const formatNumber = (value) => {
        if (!value) return "";
        // Remover todo excepto números
        const numbers = value.toString().replace(/\D/g, "");
        // Agregar separadores de miles con puntos
        return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    // Función para obtener el valor numérico limpio
    const getCleanNumber = (value) => {
        if (!value) return 0;
        return Number(value.toString().replace(/\./g, ""));
    };

    // Función para formatear números con puntos (formato colombiano) - consistente en todos los dispositivos
    const formatCurrency = (value) => {
        if (!value && value !== 0) return "";
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    // Función para obtener el label dinámico según el estado
    const getLabelDinamico = () => {
        const montoLimpio = getCleanNumber(montoTotal);

        if (!montoTotal || montoLimpio === 0) {
            return "¿Con cuánto dinero cuentas hoy?";
        }
        if (saldoRestante === 0 && montoLimpio > 0) {
            return "¡Perfecto! Todo distribuido";
        }
        if (saldoRestante > 0) {
            return "¡Esto es lo que puedes repartir!";
        }
        if (saldoRestante < 0) {
            return "¡Te excediste del presupuesto!";
        }
        return "¿Con cuánto dinero cuentas hoy?";
    };

    // Función para obtener la clase CSS del header según el estado
    const getHeaderClass = () => {
        const montoLimpio = getCleanNumber(montoTotal);

        if (!montoTotal || montoLimpio === 0) {
            return "sai-header";
        }
        if (saldoRestante === 0 && montoLimpio > 0) {
            return "sai-header sai-header-success";
        }
        if (saldoRestante < 0) {
            return "sai-header sai-header-error";
        }
        if (saldoRestante > 0) {
            return "sai-header sai-header-progress";
        }
        return "sai-header";
    };

    // saldo restante general
    const saldoRestante =
        getCleanNumber(montoTotal) -
        proveedores.reduce((acum, p) => acum + (parseInt(p.abono) || 0), 0);

    // abrir modal y cargar abono inicial
    const handleOpenModal = (idx) => {
        setModalIdx(idx);
        setAbonoSim(proveedores[idx].abono ? formatNumber(proveedores[idx].abono) : "");
    };

    // cerrar modal
    const handleCloseModal = () => {
        setModalIdx(null);
        setAbonoSim("");
    };

    // actualizar abono simulado en el modal
    const handleAbonoSimChange = (e) => {
        const value = e.target.value;
        const formattedValue = formatNumber(value);
        setAbonoSim(formattedValue);
    };

    // guardar abono simulado en proveedor (SIN modificar el monto total)
    const handleGuardarAbono = () => {
        if (modalIdx === null) return;

        const nuevoAbono = getCleanNumber(abonoSim);

        // Solo actualizar el proveedor, NO el monto total
        setProveedores((prev) =>
            prev.map((p, idx) =>
                idx === modalIdx ? { ...p, abono: nuevoAbono } : p
            )
        );

        handleCloseModal();
    };

    // validación: no puede superar saldo restante + el abono actual (si lo tiene)
    const getTopeAbono = () => {
        if (modalIdx === null) return 0;
        return saldoRestante + (proveedores[modalIdx].abono || 0);
    };

    // saldo simulado en el modal
    const getSimSaldo = () => {
        if (modalIdx === null) return 0;
        return proveedores[modalIdx].deuda - getCleanNumber(abonoSim);
    };

    // reiniciar toda la simulación
    const handleReiniciarSimulacion = () => {
        setProveedores(prev => prev.map(p => ({ ...p, abono: 0 })));
        setMontoTotal("");
    };

    // verificar si hay abonos simulados
    const hayAbonosSimulados = () => {
        return proveedores.some(p => p.abono && p.abono > 0);
    };

    // validar si el abono excede la deuda del proveedor
    const abonoExcedeDeuda = () => {
        if (modalIdx === null) return false;
        return getCleanNumber(abonoSim) > proveedores[modalIdx].deuda;
    };

    // Drag and drop handlers
    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const newList = Array.from(proveedores);
        const [moved] = newList.splice(result.source.index, 1);
        newList.splice(result.destination.index, 0, moved);
        setProveedores(newList);
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <Header />
            <div className="sai-container">
                <div className={`${getHeaderClass()} sai-card`}>
                    <label className="sai-title">{getLabelDinamico()}</label>
                    <input
                        type="text"
                        className="sai-input"
                        value={
                            saldoRestante === 0 && getCleanNumber(montoTotal) > 0 
                                ? "$ 0" 
                                : saldoRestante > 0 
                                    ? formatNumber(saldoRestante.toString()) 
                                    : montoTotal
                        }
                        onChange={(e) => setMontoTotal(formatNumber(e.target.value))}
                        placeholder="$ 1.000.000"
                        readOnly={saldoRestante !== getCleanNumber(montoTotal) && saldoRestante >= 0}
                    />
                </div>

                {/* Enlace de reiniciar - solo aparece cuando hay abonos */}
                {hayAbonosSimulados() && (
                    <div className="sai-reset-link-container">
                        <button
                            className="sai-reset-link"
                            onClick={handleReiniciarSimulacion}
                        >
                            Reiniciar simulación
                        </button>
                    </div>
                )}

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="proveedores">
                        {(provided) => (
                            <div
                                className="sai-proveedores-lista"
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                {proveedores.map((prov, idx) => (
                                    <Draggable
                                        key={prov.id}
                                        draggableId={prov.id.toString()}
                                        index={idx}
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={`sai-card sai-draggable-card ${snapshot.isDragging ? "dragging" : ""
                                                    }`}
                                                onClick={() => handleOpenModal(idx)}
                                            >
                                                <div className="sai-card-row">
                                                    <span
                                                        className="sai-card-drag"
                                                        title="Arrastra para reordenar"
                                                    >
                                                        <FontAwesomeIcon icon={faGripVertical} />
                                                    </span>
                                                    <span className="sai-card-nombre">{prov.nombre}</span>
                                                    <span className="sai-card-monto">
                                                        $ {formatCurrency(prov.deuda - (prov.abono || 0))}
                                                    </span>
                                                </div>
                                                <div className="sai-card-abono">
                                                    <p role="img" aria-label="Deuda" className="sai-card-icon">
                                                        <FontAwesomeIcon icon={faWallet} />
                                                    </p>
                                                    <p>
                                                        Deuda total:
                                                    </p>
                                                    <p>
                                                        $ {formatCurrency(prov.deuda)}
                                                    </p>
                                                </div>
                                                <div className="sai-card-saldo-sim">
                                                    <p role="img" aria-label="Dinero" className="sai-card-icon">
                                                        <FontAwesomeIcon icon={faMoneyBillWave} />
                                                    </p>
                                                    <p>
                                                        Abono simulado:
                                                    </p>
                                                    <p id="sai-card-abono-amount">
                                                        $ {formatCurrency(prov.abono)}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                {/* Modal */}
                {modalIdx !== null && (
                    <div className="sai-modal-bg" onClick={handleCloseModal}>
                        <div
                            className="sai-modal"
                            onClick={(e) => e.stopPropagation()}
                            tabIndex={-1}
                        >
                            <div className="sai-modal-title">
                                {proveedores[modalIdx].nombre}
                            </div>
                            <div className="sai-modal-label">Deuda actual:</div>
                            <div className="sai-modal-p">
                                $ {formatCurrency(proveedores[modalIdx].deuda)}
                            </div>
                            <div className="sai-modal-label">Simular abono de:</div>
                            <input
                                className={`sai-modal-input ${abonoExcedeDeuda() ? 'error-border' : ''}`}
                                type="text"
                                value={abonoSim}
                                onChange={handleAbonoSimChange}
                                placeholder="Monto a simular"
                                autoFocus
                            />
                            {(getCleanNumber(abonoSim) > getTopeAbono()) && (
                                <div className="sai-modal-p-sub error">
                                    El monto excede el disponible para la simulación
                                </div>
                            )}
                            {abonoExcedeDeuda() && (getCleanNumber(abonoSim) <= getTopeAbono()) && (
                                <div className="sai-modal-p-sub warning">
                                    El abono es mayor a la deuda. El proveedor quedaría con saldo a favor.
                                </div>
                            )}
                            <div className="sai-modal-label">Saldo proyectado tras abono:</div>
                            <div className="sai-modal-p">
                                $ {formatCurrency(getSimSaldo())}
                            </div>

                            <div className="sai-modal-btns">
                                <button className="sai-modal-btn-outline" onClick={handleCloseModal}>
                                    Cancelar
                                </button>
                                <button
                                    className="sai-modal-btn"
                                    onClick={handleGuardarAbono}
                                    disabled={getCleanNumber(abonoSim) > getTopeAbono()}
                                >
                                    Aplicar a simulación
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <NavInf />
            <Footer navinf={true} />
        </>
    );
};

export default SAI;